import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { quoteService } from '../services/quoteService';
import type { Quote } from '../types/quotes';

const QuoteAccepted: React.FC = () => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('quoteId');
    if (id) {
      setQuoteId(id);
      loadQuote(id);
    } else {
      setLoading(false);
    }
  }, []);

  const loadQuote = async (id: string) => {
    try {
      const quoteData = await quoteService.getQuote(id);
      setQuote(quoteData);
    } catch (error) {
      console.error('Error loading quote:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  if (!quoteId || !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <p className="text-gray-600">Kein Angebot gefunden.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Zurück zum Kalkulator →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 md:p-12">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-25"></div>
            <div className="relative bg-green-500 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-white" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Mega! Dein Angebot ist in trockenen Tüchern! 🎉
          </h1>
          <p className="text-lg text-gray-600">
            Wir freuen uns total, mit dir zu arbeiten!
          </p>
        </div>

        {/* Quote Summary */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
            Dein Angebot im Überblick
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Gesamtpreis:</span>
              <span className="text-2xl font-bold text-green-600">
                €{quote.amountTotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Stunden:</span>
              <span className="font-medium text-gray-900">{quote.totalHours}h</span>
            </div>
            {quote.discountPercent && quote.discountPercent > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Partnerrabatt:</span>
                <span className="font-medium text-green-600">
                  -{quote.discountPercent}% (€{quote.discountAmount?.toFixed(2)})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">
                Was kommt als Nächstes?
              </h3>
              <p className="text-sm text-blue-800 mb-3">
                Damit wir direkt loslegen können, brauch' wir noch ein paar mehr Infos von dir.
                Füll dazu einfach den Kick-Off Fragebogen aus – dauert nur 5 Minuten, versprochen! 🚀
              </p>
              <p className="text-xs text-blue-700">
                Danach können wir direkt mit deinem Projekt starten und du bekommst von uns eine
                Bestätigung mit allen Details.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => window.location.href = `/kickoff?quoteId=${quoteId}`}
            className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-green-700 hover:to-blue-700 transition-all transform hover:scale-105 flex items-center justify-center gap-3 shadow-lg"
          >
            Zum Kick-Off Fragebogen
            <ArrowRight className="w-6 h-6" />
          </button>
          
          <button
            onClick={() => window.location.href = '/my-orders'}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
          >
            Zu meinen Bestellungen
          </button>
        </div>

        {/* Optional: Back to calculator link */}
        <div className="text-center mt-6">
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Zurück zum Kalkulator →
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Du hast Fragen? Schreib uns einfach eine E-Mail an{' '}
            <a href="mailto:emanuel@chimperator-live.de" className="text-blue-600 hover:underline">
              emanuel@chimperator-live.de
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteAccepted;

